import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Plane, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Page";

const CAT = {
  urlaub: { label: "Urlaub", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  krankheit: { label: "Krankheit", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  sonderurlaub: { label: "Sonderurlaub", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sonstige: { label: "Sonstige", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const STATUS = {
  pending: { label: "Wartet", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  approved: { label: "Genehmigt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Abgelehnt", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function Urlaub() {
  const [vac, setVac] = useState([]);
  const [sick, setSick] = useState([]);
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ type: "urlaub", start_date: "", end_date: "", note: "", half_day: false, half_day_period: "vormittag" });

  const loadVac = async () => setVac((await api.get("/vacations")).data || []);
  const loadSick = async () => setSick((await api.get("/sick-leaves")).data || []);
  const loadSum = async () => setSum((await api.get("/vacations/summary")).data);
  useEffect(() => { loadVac(); loadSick(); loadSum(); }, []);

  const submit = async () => {
    if (!form.start_date || !form.end_date) return toast.error("Bitte Zeitraum wählen");
    try {
      if (form.type === "krankheit") {
        await api.post("/sick-leaves", { start_date: form.start_date, end_date: form.end_date, note: form.note });
        toast.success("Krankmeldung übermittelt");
        loadSick();
      } else {
        await api.post("/vacations", {
          start_date: form.start_date, end_date: form.end_date, note: form.note,
          category: form.type, half_day: form.half_day, half_day_period: form.half_day_period,
        });
        toast.success("Antrag gestellt");
        loadVac();
      }
      loadSum();
      setOpen(false);
      setForm({ type: "urlaub", start_date: "", end_date: "", note: "", half_day: false, half_day_period: "vormittag" });
    } catch (e) { toast.error(apiError(e)); }
  };

  const delVac = async (r) => {
    const msg = r._status === "approved"
      ? "Löschung dieses genehmigten Urlaubs beim Chef beantragen?"
      : "Diesen Antrag wirklich löschen?";
    if (!window.confirm(msg)) return;
    try {
      const res = await api.delete(`/vacations/${r.id}`);
      toast.success(res.data?.status === "deletion_requested" ? "Löschung beim Chef beantragt" : "Antrag gelöscht");
      loadVac(); loadSum();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rows = [
    ...vac.map((v) => ({ ...v, kind: v.category || "urlaub", _status: v.status })),
    ...sick.map((s) => ({ ...s, kind: "krankheit", _status: "reported" })),
  ].filter((r) => filter === "all" || r.kind === filter);
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = rows.filter((r) => (r.end_date || "") >= today).sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  const past = rows.filter((r) => (r.end_date || "") < today).sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));

  const renderRow = (r) => (
    <Card key={r.id} className="p-4 border-slate-200 rounded-xl flex items-center justify-between gap-3" data-testid={`absence-row-${r.id}`}>
      <div className="min-w-0">
        <div className="font-medium text-slate-900">{fmtDate(r.start_date)} – {fmtDate(r.end_date)}</div>
        {r.half_day && <div className="text-xs text-purple-600 mt-0.5">Halber Tag{r.half_day_period ? ` (${r.half_day_period === "vormittag" ? "Vormittag" : "Nachmittag"})` : ""}</div>}
        {r.note && <div className="text-sm text-slate-500 mt-0.5">{r.note}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="outline" className={CAT[r.kind]?.cls}>{CAT[r.kind]?.label || r.kind}</Badge>
        {r.kind !== "krankheit" && <Badge variant="outline" className={STATUS[r._status]?.cls}>{STATUS[r._status]?.label || r._status}</Badge>}
        {r.kind !== "krankheit" && r.source !== "company_holiday" && (
          r.deletion_requested
            ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Löschung beantragt</Badge>
            : <Button size="sm" variant="ghost" className="text-slate-400 hover:text-rose-600 h-8 w-8 p-0" data-testid={`absence-delete-${r.id}`} onClick={() => delVac(r)} title={r._status === "approved" ? "Löschung beantragen" : "Löschen"}><Trash2 className="h-4 w-4" /></Button>
        )}
      </div>
    </Card>
  );

  const isVac = form.type !== "krankheit";

  return (
    <div>
      <PageHeader
        title="Abwesenheit"
        subtitle="Beantrage Urlaub, melde Krankheit oder sonstige Abwesenheiten."
        testid="abwesenheit-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="absence-open" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Antrag</Button>
            </DialogTrigger>
            <DialogContent data-testid="absence-dialog">
              <DialogHeader><DialogTitle>Abwesenheit beantragen</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Art</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="absence-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urlaub">Urlaub</SelectItem>
                      <SelectItem value="krankheit">Krankheit</SelectItem>
                      <SelectItem value="sonderurlaub">Sonderurlaub</SelectItem>
                      <SelectItem value="sonstige">Sonstige Abwesenheit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Von</Label><Input type="date" data-testid="absence-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Bis</Label><Input type="date" data-testid="absence-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Anmerkung</Label><Input data-testid="absence-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional" /></div>
                {isVac && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer">
                      <input type="checkbox" data-testid="absence-halfday" checked={!!form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                      Halber Tag (zählt 0,5 Tage)
                    </label>
                    {form.half_day && (
                      <div className="flex gap-4 text-sm text-slate-600 pl-6" data-testid="absence-period">
                        <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name="halfperiod" checked={form.half_day_period === "vormittag"} onChange={() => setForm({ ...form, half_day_period: "vormittag" })} /> Vormittag</label>
                        <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name="halfperiod" checked={form.half_day_period === "nachmittag"} onChange={() => setForm({ ...form, half_day_period: "nachmittag" })} /> Nachmittag</label>
                      </div>
                    )}
                  </>
                )}
              </div>
              <DialogFooter><Button onClick={submit} data-testid="absence-submit" className="brand-bg text-white hover:opacity-90">Antrag stellen</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {sum && (
        <>
          <div className={`grid grid-cols-2 ${sum.carryover_enabled ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 mb-3`} data-testid="vacation-summary">
            <Card className="p-4 border-slate-200 rounded-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Offen</div>
              <div className="text-2xl font-bold text-emerald-600 mt-1" data-testid="vac-remaining">{sum.remaining}</div>
              <div className="text-xs text-slate-400 mt-0.5">von {sum.total} Urlaubstagen {sum.year}</div>
            </Card>
            <Card className="p-4 border-slate-200 rounded-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">In Bearbeitung</div>
              <div className="text-2xl font-bold text-purple-600 mt-1" data-testid="vac-pending">{sum.pending}</div>
              <div className="text-xs text-slate-400 mt-0.5">Urlaub beantragt</div>
            </Card>
            <Card className="p-4 border-slate-200 rounded-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Genommen</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="vac-taken">{sum.taken}</div>
              <div className="text-xs text-slate-400 mt-0.5">Urlaub genehmigt {sum.year}</div>
            </Card>
            <Card className="p-4 border-slate-200 rounded-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Anspruch / Jahr</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="vac-total">{sum.total}</div>
              <div className="text-xs text-slate-400 mt-0.5">Urlaubstage gesamt</div>
            </Card>
            {sum.carryover_enabled && (
              <Card className="p-4 border-slate-200 rounded-xl bg-amber-50/50">
                <div className="text-xs font-semibold uppercase tracking-wider text-amber-600">Übertrag Vorjahr</div>
                <div className="text-2xl font-bold text-amber-700 mt-1" data-testid="vac-carryover">{sum.carryover}</div>
                <div className="text-xs text-slate-400 mt-0.5">aus {sum.year - 1}</div>
              </Card>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-5" data-testid="absence-other-summary">
            <Card className="p-4 border-slate-200 rounded-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-rose-500">Krankheit</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="sum-krank">{sum.krank}</div>
              <div className="text-xs text-slate-400 mt-0.5">Tage {sum.year}</div>
            </Card>
            <Card className="p-4 border-slate-200 rounded-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-600">Sonderurlaub</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="sum-sonderurlaub">{sum.sonderurlaub}</div>
              <div className="text-xs text-slate-400 mt-0.5">Tage {sum.year}</div>
            </Card>
            <Card className="p-4 border-slate-200 rounded-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sonstige</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="sum-sonstige">{sum.sonstige}</div>
              <div className="text-xs text-slate-400 mt-0.5">Tage {sum.year}</div>
            </Card>
          </div>
        </>
      )}

      <div className="space-y-6">
        <div className="flex flex-wrap gap-2" data-testid="absence-filter">
          {[["all", "Alle"], ["urlaub", "Urlaub"], ["krankheit", "Krankheit"], ["sonderurlaub", "Sonderurlaub"], ["sonstige", "Sonstige"]].map(([val, lbl]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              data-testid={`filter-${val}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === val ? "brand-bg text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            >
              {lbl}
            </button>
          ))}
        </div>
        {rows.length === 0 ? (
          <Card className="border-slate-200 rounded-xl"><EmptyState icon={Plane} title="Keine Abwesenheiten" testid="absence-empty" /></Card>
        ) : (
          <>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2" data-testid="absence-upcoming-title">Anstehend & aktuell ({upcoming.length})</h3>
              <div className="grid gap-3">
                {upcoming.length === 0 ? (
                  <Card className="border-slate-200 rounded-xl border-dashed"><div className="p-4 text-sm text-slate-400 text-center">Nichts anstehend.</div></Card>
                ) : upcoming.map(renderRow)}
              </div>
            </div>
            {past.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2" data-testid="absence-past-title">Vergangene ({past.length})</h3>
                <div className="grid gap-3">{past.map(renderRow)}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
